import React, {useState} from "react";
import {Link, Navigate} from "react-router-dom";
import {
	Alert,
	Button,
	FormGroup,
	Form,
	Container,
	Row,
	Col,
} from "react-bootstrap";
import { Formik } from "formik";
import { useAuth } from "../../AuthUser";
import API from "../../api";
import {Helmet} from "react-helmet";

const Signup = () => {
	const { token } = useAuth();
	const [sentTo, setSentTo] = useState(null);
	const [submitError, setSubmitError] = useState(null);

	if (token) {
		return <Navigate to="/account" replace />;
	}

	// On-page confirmation instead of a redirect to /signin with a vanishing
	// toast (MGDWD-159 feedback).
	if (sentTo) {
		return (
			<>
				<Helmet>
					<title>Forgot Password | MyGolfDNA.com</title>
					<meta name="description" content="reset your password"/>
				</Helmet>
				<Container>
					<Row>
						<Col sm={12} md={{ span: 6, offset: 3 }} lg={{ span: 6, offset: 3 }} xl={{ span: 4, offset: 4 }}>
							<div className="text-center mt-5">
								<h2 className="text-white">Check your inbox</h2>
								<p className="text-white-50 mt-3">
									If an account exists for <span className="text-white">{sentTo}</span>,
									we've sent password reset instructions. The link expires in 1 hour.
								</p>
								<p className="text-white-50 small">
									Nothing after a few minutes? Check spam, or try again.
								</p>
								<Button variant="link" as={Link} to="/signin" size="sm" className="text-light text-opacity-75">
									Back to Sign in
								</Button>
							</div>
						</Col>
					</Row>
				</Container>
			</>
		);
	}

	return (
		<Formik
			initialValues={{ email: ""}}
			validate={(values) => {
				const errors = {};
				if (!values.email) {
					errors.email = "Required";
				} else if (
					!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)
				) {
					errors.email = "Invalid email address";
				}

				return errors;
			}}
			onSubmit={(values, { setSubmitting, setErrors }) => {
				setSubmitError(null);
				setTimeout(() => {
					API.post("auth/forgot-password", {
						email: values.email,
					})
						.then(() => {
							setSentTo(values.email);
							setSubmitting(false);
						})
						.catch((error) => {
							setSubmitting(false);
							setSubmitError(error.response?.data?.message || "Something went wrong. Please try again.");
							if (
								error.response &&
								error.response.data.errors &&
								error.response.data.errors.length
							) {
								const result = error.response.data.errors.reduce(function (
										map,
										obj
									) {
										map[obj.property] = obj.message;
										return map;
									},
									{});
								setErrors(result);
							}
						});
				}, 400);
			}}
		>
			{({
					values,
					errors,
					touched,
					handleChange,
					handleSubmit,
					isSubmitting,
				}) => (
				<>
					<Helmet>
						<title>Forgot Password | MyGolfDNA.com</title>
						<meta name="description" content="reset your password"/>
					</Helmet>
					<Container>
						<Row>
							<Col sm={12} md={{ span: 6, offset: 3 }} lg={{ span: 6, offset: 3 }} xl={{ span: 4, offset: 4 }}>
								<h2 className="text-white text-center mt-5">Forgot password</h2>
								{submitError && (
									<Alert variant="danger" className="mt-4 mb-0">{submitError}</Alert>
								)}
								<Form noValidate className="mt-5 form-group" onSubmit={handleSubmit}>
									<Form.Group className="form-floating mb-3">
										<Form.Control
											className="bg-dark border-light border-opacity-25 text-light"
											placeholder="Email address"
											type="email"
											autoComplete="email"
											name="email"
											id="email"
											onChange={handleChange}
											value={values.email}
											isInvalid={errors.email && touched.email}
											isValid={!errors.email && touched.email}
										/>
										<Form.Label className="text-white" htmlFor="email">Email address</Form.Label>
										<div className="invalid-feedback">{errors.email}</div>
									</Form.Group>
									<FormGroup className="mt-5">
										<div className="d-grid gap-4">
											<Button variant="secondary" size="lg" type="submit" disabled={isSubmitting}>
												{isSubmitting ? (
													<>
													<span
														className="spinner-border spinner-border-sm"
														role="status"
														aria-hidden={true}
													/>{" "}
													</>
												) : (
													"Send reset password instructions"
												)}
											</Button>
											<Button variant="link" as={Link} to="/signin" size="sm" className="text-light text-opacity-75">Already have an account? Sign in</Button>
										</div>
									</FormGroup>
								</Form>
							</Col>
						</Row>
					</Container>
				</>
			)}
		</Formik>
	);
};

export default Signup;
